import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getTheme, injectFonts, onThemeChange, transitionTheme, T } from '../utils/fonts';

/** Sun/moon switch. aria-pressed rather than a role=switch so it reads as a
    plain toggle button; the label always names the action, not the state. */
const ThemeToggle = ({ compact }) => {
  const [theme, setTheme] = useState(() => (typeof document === 'undefined' ? 'dark' : getTheme()));
  const [changing, setChanging] = useState(false);

  useEffect(() => onThemeChange((e) => setTheme(e.detail)), []);

  const next = theme === 'light' ? 'dark' : 'light';
  const label = next === 'light' ? 'עבור למצב בהיר' : 'עבור למצב כהה';

  const handleToggle = async (event) => {
    if (changing) return;
    setChanging(true);
    // Synthesised mobile clicks can report clientX/clientY as 0 in production.
    // The button rectangle is stable across touch, mouse and keyboard input and
    // guarantees that the wipe always opens from the visible sun/moon control.
    const rect = event.currentTarget.getBoundingClientRect();
    const pointerOrigin = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
    try {
      await transitionTheme(next, pointerOrigin);
    } finally {
      setChanging(false);
    }
  };

  return (
    <button
      type="button"
      className={`theme-toggle${compact ? ' is-compact' : ''}${changing ? ' is-changing' : ''}`}
      onClick={handleToggle}
      aria-label={label}
      title={label}
      aria-pressed={theme === 'light'}
      aria-busy={changing}
    >
      <span className="theme-toggle-icon" aria-hidden="true">{theme === 'light' ? '☀' : '☾'}</span>
    </button>
  );
};

const Navbar = () => {
  const [isOpen, setIsOpen]   = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const closeMenu = () => setIsOpen(false);
  const toggleMenu = () => setIsOpen(o => !o);

  useEffect(() => { injectFonts(); }, []);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape' && isOpen) closeMenu(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { to: '/',             label: 'דף הבית',  ariaLabel: 'עבור לדף הבית' },
    { to: '/courses',      label: 'הקורסים',  ariaLabel: 'עבור לדף הקורסים' },
    { to: '/about',        label: 'אודות',    ariaLabel: 'עבור לדף אודות' },
    { to: '/contact',      label: 'צור קשר',  ariaLabel: 'עבור לדף צור קשר' },
    { to: '/accessibility',label: 'נגישות',   ariaLabel: 'עבור להצהרת נגישות' },
  ];

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-16px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
        @keyframes logoShimmer {
          0%   { background-position: -200% center; }
          100% { background-position:  200% center; }
        }
        @keyframes borderSlide {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes themeToggleSpin {
          0%   { transform: rotate(0deg) scale(1); }
          45%  { transform: rotate(150deg) scale(0.58); }
          70%  { transform: rotate(300deg) scale(1.18); }
          100% { transform: rotate(360deg) scale(1); }
        }

        /* ── Logo ── */
        .logo-link {
          text-decoration: none;
          padding: 6px 4px;
          border-radius: 6px;
          font-family: ${T.fontDisplay};
          font-weight: 700;
          background: linear-gradient(90deg, ${T.text} 10%, ${T.godot} 45%, ${T.violet} 70%, ${T.text} 95%);
          background-size: 250% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: logoShimmer 4s linear infinite;
          transition: opacity 0.3s ease;
        }
        .logo-link:hover { opacity: 0.85; }
        .logo-link:focus-visible { outline: 3px solid ${T.focus}; outline-offset: 4px; border-radius: 4px; }

        /* ── Desktop nav link ── */
        .nav-link {
          position: relative;
          text-decoration: none;
          color: ${T.muted};
          font-family: ${T.fontBody};
          font-size: clamp(0.9rem,2.5vw,1.05rem);
          font-weight: 600;
          padding: 8px 18px;
          border-radius: 10px;
          transition: color 0.25s ease, background 0.25s ease;
          white-space: nowrap;
          display: inline-block;
          overflow: hidden;
        }
        .nav-link::after {
          content: '';
          position: absolute;
          bottom: 4px; left: 50%; right: 50%;
          height: 2px;
          background: linear-gradient(90deg, ${T.godot}, ${T.violet});
          border-radius: 2px;
          transition: left 0.3s ease, right 0.3s ease;
        }
        .nav-link:hover, .nav-link:focus { color: ${T.text}; background: ${T.godotSoft}; outline: none; }
        .nav-link:hover::after, .nav-link:focus::after, .nav-link.active::after { left: 16px; right: 16px; }
        .nav-link.active { color: ${T.text}; background: ${T.godotSoft}; }
        .nav-link:focus-visible { outline: 3px solid ${T.focus}; outline-offset: 2px; }

        /* ── Hamburger ── */
        .hamburger-btn {
          display: none;
          cursor: pointer;
          font-size: clamp(1.3rem,5vw,1.7rem);
          background: ${T.godotSoft};
          border: 1px solid ${T.line};
          color: ${T.text};
          padding: 8px 12px;
          border-radius: 10px;
          z-index: 1001;
          transition: all 0.3s ease;
          min-width: 44px; min-height: 44px;
          align-items: center; justify-content: center;
        }
        .hamburger-btn:hover, .hamburger-btn:focus { background: ${T.godotSoft}; border-color: ${T.godot}; outline: none; }
        .hamburger-btn:focus-visible { outline: 3px solid ${T.focus}; outline-offset: 2px; }

        /* ── Mobile link ── */
        .mobile-link {
          text-decoration: none;
          color: ${T.text};
          font-family: ${T.fontBody};
          font-size: clamp(1rem,4vw,1.1rem);
          font-weight: 600;
          display: block;
          padding: clamp(13px,3vw,16px) 20px;
          margin: 4px 0;
          border-radius: 10px;
          border: 1px solid ${T.lineSoft};
          background: ${T.godotSoft};
          transition: all 0.25s ease;
          text-align: right;
          direction: rtl;
          position: relative;
          overflow: hidden;
          min-height: 48px;
          display: flex;
          align-items: center;
          justify-content: flex-end;
        }
        .mobile-link::before {
          content: '';
          position: absolute;
          right: 0; top: 0; bottom: 0;
          width: 3px;
          background: linear-gradient(to bottom, ${T.godot}, ${T.violet});
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .mobile-link:hover, .mobile-link:focus, .mobile-link.active {
          background: ${T.godotSoft};
          border-color: ${T.godot};
          color: ${T.text};
          padding-right: 24px;
        }
        .mobile-link:hover::before, .mobile-link.active::before { opacity: 1; }
        .mobile-link:focus-visible { outline: 3px solid ${T.focus}; outline-offset: 2px; }

        /* ── Animated border ── */
        .navbar-border {
          position: absolute;
          bottom: 0; left: 0; right: 0; height: 1px;
          background: linear-gradient(90deg,transparent 0%,${T.godot} 30%,${T.violet} 70%,transparent 100%);
          background-size: 200% 100%;
          animation: borderSlide 4s linear infinite;
          opacity: 0.6;
        }

        .gg-nav {
          background-color: ${T.navBg};
          color: ${T.text};
          box-shadow: 0 1px 0 ${T.lineSoft};
        }
        .gg-nav.is-scrolled {
          background-color: ${T.navBgScrolled};
          box-shadow: 0 2px 32px rgba(0,0,0,0.28);
        }

        /* ── Theme toggle ── */
        .theme-toggle {
          display: inline-flex; align-items: center; justify-content: center;
          box-sizing: border-box;
          width: 44px; height: 44px;
          margin-inline-start: 6px;
          border-radius: 12px;
          border: 1px solid ${T.line};
          background: transparent;
          color: ${T.text};
          font-size: 1.15rem; line-height: 1;
          cursor: pointer;
          transition: background 0.25s ease, border-color 0.25s ease;
        }
        .theme-toggle:hover { background: ${T.godotSoft}; border-color: ${T.godot}; }
        .theme-toggle:focus-visible { outline: 3px solid ${T.focus}; outline-offset: 2px; }
        .theme-toggle-icon { display: block; transform-origin: center; }
        .theme-toggle.is-changing .theme-toggle-icon {
          animation: themeToggleSpin .62s cubic-bezier(.2,.85,.25,1) both;
        }

        .mobile-actions { display: none; align-items: center; }

        @media (min-width: 769px) {
          .desktop-links { display: flex !important; }
          .hamburger-btn  { display: none !important; }
          .mobile-actions { display: none !important; }
        }
        @media (max-width: 768px) {
          .desktop-links { display: none !important; }
          .hamburger-btn  { display: flex !important; }
          /* Reachable without opening the menu — it is a display preference,
             not navigation. */
          .mobile-actions { display: flex !important; margin-inline-start: auto; margin-inline-end: 8px; }
        }
      `}</style>

      {/* The themed colours live in the .gg-nav class, not in this inline
          style. A var() in an inline style is not re-resolved when the custom
          property changes unless the property happens to be inherited, so an
          inline background would keep the previous theme's colour after a
          switch. From a stylesheet it re-resolves correctly. */}
      <nav
        className={`gg-nav${scrolled ? ' is-scrolled' : ''}`}
        style={{
          width: '100%',
          position: 'fixed',
          top: 0, left: 0, right: 0,
          zIndex: 1000,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          transition: 'background-color 0.4s ease, box-shadow 0.4s ease',
          fontFamily: T.fontBody,
        }}
        role="navigation"
        aria-label="תפריט ניווט ראשי"
      >
        <div className="navbar-border" aria-hidden="true" />

        <div style={{
          maxWidth: '100%',
          margin: '0 auto',
          padding: 'clamp(10px,2vw,16px) clamp(16px,4vw,48px)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ fontSize:'clamp(1.1rem,4vw,1.55rem)', fontWeight:900, zIndex:1001, letterSpacing:'-0.02em' }}>
            <Link to="/" className="logo-link" aria-label="חזרה לדף הבית - Go Game Dev">
              GoGameDev3D
            </Link>
          </div>

          <div className="desktop-links" style={{ display:'flex', gap:'clamp(4px,1.5vw,8px)', alignItems:'center' }} role="menubar">
            {navLinks.map(({ to, label, ariaLabel }) => (
              <Link
                key={to} to={to}
                className={`nav-link${location.pathname === to ? ' active' : ''}`}
                role="menuitem" aria-label={ariaLabel}
              >{label}</Link>
            ))}
            <ThemeToggle />
          </div>

          <div className="mobile-actions">
            <ThemeToggle compact />
          </div>

          <button
            className="hamburger-btn"
            onClick={toggleMenu}
            aria-label={isOpen ? 'סגור תפריט' : 'פתח תפריט'}
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
            type="button"
          >
            <span aria-hidden="true" style={{ transition:'transform 0.3s ease', display:'inline-block', transform: isOpen ? 'rotate(90deg)' : 'none' }}>
              {isOpen ? '✕' : '☰'}
            </span>
          </button>
        </div>

        {isOpen && (
          <>
            <div
              style={{ position:'fixed', top:0, left:0, right:0, bottom:0, backgroundColor:'rgba(0,0,0,0.6)', backdropFilter:'blur(4px)', zIndex:999, animation:'fadeIn 0.3s ease' }}
              onClick={closeMenu} aria-hidden="true"
            />
            <div
              id="mobile-menu"
              style={{
                position:'fixed',
                top:'clamp(56px,13vw,74px)',
                right:12,
                width:'min(300px,calc(100vw - 24px))',
                backgroundColor: T.panel,
                backdropFilter:'blur(20px)',
                WebkitBackdropFilter:'blur(20px)',
                padding:'clamp(16px,4vw,24px)',
                textAlign:'right',
                boxShadow:'0 8px 40px rgba(0,0,0,0.35)',
                zIndex:1000,
                animation:'slideDown 0.3s ease',
                borderRadius:16,
                border:`1px solid ${T.line}`,
              }}
              role="menu" aria-label="תפריט ניווט מובייל"
            >
              <div style={{ height:2, background:`linear-gradient(90deg, ${T.godot}, ${T.violet})`, borderRadius:2, marginBottom:16 }} />
              {navLinks.map(({ to, label, ariaLabel }) => (
                <Link
                  key={to} to={to}
                  className={`mobile-link${location.pathname === to ? ' active' : ''}`}
                  onClick={closeMenu} role="menuitem" aria-label={ariaLabel}
                >{label}</Link>
              ))}
            </div>
          </>
        )}
      </nav>
    </>
  );
};

export default Navbar;
